import { BlinkColor, NotificationStrategy } from '@ekkogmbh/apisdk';
import { blue, cyan, green, grey, pink, red, yellow } from '@mui/material/colors';
import { Box, Chip, InputLabel, InputProps, ListItemText, MenuItem, Stack, Typography } from '@mui/material';
import { Highlight, NotInterested } from '@mui/icons-material';
import { AdvancedSection } from './AdvancedSection';
import { StyledSwitch } from './StyledSwitch';
import React, { useEffect, useMemo, useState } from 'react';
import { StyledSelectField } from 'src/Common/Components/Forms/StyledSelectField';
import { StyledTextField } from 'src/Common/Components/Forms/StyledTextField';
import useNotificationStrategy from 'src/ZoneManagement/Providers/NotificationStrategyProvider';
import ListStepper from '../ListStepper';
import { CenteredColumn, ReactiveRow } from '../Stacks';
import { InputPresets } from './InputAdditions';

interface AdornmentInputProps extends Pick<InputProps, 'type' | 'disabled' | 'onChange'> {
  label: string;
  value: string | number;
}
const AdornmentInput = ({ label, value, ...props }: AdornmentInputProps): React.JSX.Element => (
  <Stack
    direction={'row'}
    spacing={1}
    alignItems={'center'}
    sx={{
      pr: 4,
      opacity: props.disabled ? 0.4 : 1,
    }}
  >
    <InputLabel
      sx={{
        position: 'relative',
        transform: 'none',
        color: 'text.secondary',
        fontSize: '0.875rem',
      }}
    >
      {label}
    </InputLabel>
    <input
      {...props}
      value={value}
      style={{
        width: 40,
        border: 'none',
        background: 'transparent',
        fontSize: '0.875rem',
        textAlign: 'center',
        outline: 'none',
      }}
    />
  </Stack>
);

const BlinkColorMap: Record<string, string> = {
  [BlinkColor.BLUE]: blue[500],
  [BlinkColor.CYAN]: cyan[500],
  [BlinkColor.GREEN]: green[500],
  [BlinkColor.YELLOW]: yellow[500],
  [BlinkColor.RED]: red[500],
  [BlinkColor.MAGENTA]: pink[500],
  [BlinkColor.WHITE]: grey[300],
};

interface Props {
  optional?: boolean;
  /** Label describing what the strategy inherits from when not set (e.g., "Zone", "Parent") */
  inheritLabel?: string;
  showDisablePrevious?: boolean;
  showIgnoreQueue?: boolean;
}
export const NotificationStrategyForm = ({
  optional,
  inheritLabel,
  showDisablePrevious = true,
  showIgnoreQueue = true,
}: Props): React.JSX.Element => {
  const {
    _strategies: strategies,
    addStrategy,
    removeStrategy,
    setStrategy,
    setStrategies,
  } = useNotificationStrategy();
  const isOptional = useMemo((): boolean => optional === true, [optional]);
  const isInheriting = useMemo((): boolean => isOptional && strategies.length === 0, [isOptional, strategies.length]);

  const [currentIndex, setCurrentIndex] = useState<number | undefined>();
  useEffect(() => {
    // fix index if strategies change to something unexpected
    if (currentIndex === undefined) {
      if (strategies.length > 0) {
        setCurrentIndex(0);
      }
    } else {
      if (currentIndex >= strategies.length) {
        setCurrentIndex(strategies.length - 1);
      }
    }

    // add default LIGHT strategy for non-optional case
    if (!isOptional && strategies.length === 0) {
      addStrategy(NotificationStrategy.LIGHT);
      setCurrentIndex(0);
    }
  }, [strategies, isOptional]);
  const currentStrategy = useMemo(() => {
    if (currentIndex !== undefined) {
      return strategies[currentIndex];
    }

    return undefined;
  }, [currentIndex, strategies]);

  const configForm = useMemo(() => {
    if (currentStrategy === undefined || currentIndex === undefined) {
      return <></>;
    }

    const { configuration } = currentStrategy;

    switch (currentStrategy.name) {
      case NotificationStrategy.LIGHT: {
        const { duration, color, redirectTarget, redirectDepth, bypassQueue, disablePrevious } = configuration;
        const hasAdvancedSettings = bypassQueue || disablePrevious || redirectTarget;

        return (
          <CenteredColumn width={'100%'}>
            <ReactiveRow width={'100%'}>
              <InputPresets
                onClick={(value) => {
                  setStrategy(currentIndex, {
                    ...currentStrategy,
                    configuration: {
                      ...configuration,
                      color: value,
                    },
                  });
                }}
                options={Object.entries(BlinkColorMap).map(([colorKey, colorValue]) => ({
                  value: colorKey,
                  backgroundColor: colorValue,
                }))}
                selected={String(color)}
              >
                <StyledTextField
                  label="Color"
                  value={color ? String(color).charAt(0).toUpperCase() + String(color).slice(1).toLowerCase() : 'Red'}
                  InputProps={{
                    readOnly: true,
                    startAdornment: (
                      <Box
                        sx={{
                          width: 20,
                          height: 20,
                          borderRadius: 0.5,
                          backgroundColor: BlinkColorMap[(color as BlinkColor) ?? BlinkColor.RED],
                          mr: 1,
                        }}
                      />
                    ),
                  }}
                />
              </InputPresets>
              <InputPresets
                selected={parseInt(duration as string)}
                onClick={(value) => {
                  setStrategy(currentIndex, {
                    ...currentStrategy,
                    configuration: {
                      ...configuration,
                      duration: String(value),
                    },
                  });
                }}
                options={[
                  { label: '1m', value: 60 },
                  { label: '5m', value: 300 },
                  { label: '30m', value: 1800 },
                  { label: '1h', value: 3600 },
                  { label: '8h', value: 28800 },
                ]}
              >
                <StyledTextField
                  type={'number'}
                  label={'Duration'}
                  value={String(duration) ?? '0'}
                  onChange={(e) => {
                    setStrategy(currentIndex, {
                      ...currentStrategy,
                      configuration: {
                        ...configuration,
                        duration: parseInt(e.target.value) < 0 ? '0' : String(e.target.value),
                      },
                    });
                  }}
                  endAdornment={
                    <Typography variant="body2" color="textSecondary">
                      sec
                    </Typography>
                  }
                />
              </InputPresets>
            </ReactiveRow>
            <AdvancedSection
              hasSettings={!!hasAdvancedSettings}
              helpTitle="Light Notification"
              helpSubtitle="Advanced Options"
              helpMaxWidth="md"
              helpContent={
                <Stack spacing={2}>
                  {showDisablePrevious && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5, mb: 0.5 }}
                      >
                        Stop Previous Blink
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                        Automatically stops blinking from the previous finder result when a new search is made.
                      </Typography>
                    </Box>
                  )}
                  {showIgnoreQueue && (
                    <Box>
                      <Typography
                        variant="subtitle2"
                        fontWeight={600}
                        sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5, mb: 0.5 }}
                      >
                        Ignore Queue
                      </Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ pl: 2 }}>
                        Ignore device queue and force send notification immediately.
                      </Typography>
                    </Box>
                  )}
                  <Box>
                    <Typography
                      variant="subtitle2"
                      fontWeight={600}
                      sx={{ borderBottom: 1, borderColor: 'divider', pb: 0.5, mb: 0.5 }}
                    >
                      Redirect
                    </Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ pl: 2, mb: 2 }}>
                      Instead of blinking the device at the compartment, blink a device higher up in the coordinate
                      tree.
                    </Typography>
                    <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} sx={{ pl: 2 }}>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="primary" fontWeight={600}>
                            Direct
                          </Typography>
                          <Chip
                            label="default"
                            size="small"
                            variant="outlined"
                            sx={{ height: 18, fontSize: '0.65rem' }}
                          />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Blinks the device directly at the compartment.
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            backgroundColor: 'action.hover',
                            p: 1,
                            borderRadius: 1,
                            borderLeft: 3,
                            borderColor: 'primary.main',
                            overflow: 'auto',
                            m: 0,
                          }}
                        >
                          {`Warehouse
└─ HallA
   └─ Aisle1
      └─ ShelfB
         └─ Slot3 `}
                          <Highlight
                            sx={{
                              fontSize: '1rem',
                              verticalAlign: 'text-bottom',
                              color: 'text.primary',
                            }}
                          />
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="primary" fontWeight={600}>
                            Parent Node
                          </Typography>
                          <Chip label="depth 2" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Redirects 2 levels up from the compartment.
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            backgroundColor: 'action.hover',
                            p: 1,
                            borderRadius: 1,
                            borderLeft: 3,
                            borderColor: 'primary.main',
                            overflow: 'auto',
                            m: 0,
                          }}
                        >
                          {`Warehouse
└─ HallA
   └─ Aisle1 `}
                          <Highlight
                            sx={{
                              fontSize: '1rem',
                              verticalAlign: 'text-bottom',
                              color: 'text.primary',
                            }}
                          />
                          {`
      └─ ShelfB
         └─ Slot3`}
                        </Box>
                      </Box>
                      <Box sx={{ flex: 1 }}>
                        <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 0.5 }}>
                          <Typography variant="caption" color="primary" fontWeight={600}>
                            Common Coordinate
                          </Typography>
                          <Chip label="depth 1" size="small" sx={{ height: 18, fontSize: '0.65rem' }} />
                        </Stack>
                        <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                          Blinks at the shared parent of multiple results.
                        </Typography>
                        <Box
                          component="pre"
                          sx={{
                            fontFamily: 'monospace',
                            fontSize: '0.75rem',
                            backgroundColor: 'action.hover',
                            p: 1,
                            borderRadius: 1,
                            borderLeft: 3,
                            borderColor: 'primary.main',
                            overflow: 'auto',
                            m: 0,
                          }}
                        >
                          {`Warehouse
└─ HallA `}
                          <Highlight
                            sx={{
                              fontSize: '1rem',
                              verticalAlign: 'text-bottom',
                              color: 'text.primary',
                            }}
                          />
                          {`
   ├─ Aisle1
   │  └─ Slot3
   └─ Aisle2
      └─ Slot7`}
                        </Box>
                      </Box>
                    </Stack>
                  </Box>
                </Stack>
              }
            >
              {(showDisablePrevious || showIgnoreQueue) && (
                <Stack direction="row" gap={1}>
                  {showDisablePrevious && (
                    <Box sx={{ flex: 1 }}>
                      <StyledSwitch
                        label="Stop Previous Blink"
                        value={Boolean(disablePrevious)}
                        onChange={() => {
                          setStrategy(currentIndex, {
                            ...currentStrategy,
                            configuration: {
                              ...configuration,
                              disablePrevious: !disablePrevious,
                            },
                          });
                        }}
                      />
                    </Box>
                  )}
                  {showIgnoreQueue && (
                    <Box sx={{ flex: 1 }}>
                      <StyledSwitch
                        label="Ignore Queue"
                        value={Boolean(bypassQueue)}
                        onChange={() => {
                          setStrategy(currentIndex, {
                            ...currentStrategy,
                            configuration: {
                              ...configuration,
                              bypassQueue: !bypassQueue,
                            },
                          });
                        }}
                      />
                    </Box>
                  )}
                </Stack>
              )}
              <StyledSelectField
                label={'Redirect'}
                value={(redirectTarget ?? 'direct') as string}
                onChange={(e) => {
                  const value = e.target.value as string;
                  if (value === 'direct') {
                    const changedConfig = configuration;
                    delete changedConfig.redirectTarget;
                    delete changedConfig.redirectDepth;

                    setStrategy(currentIndex, {
                      ...currentStrategy,
                      configuration: changedConfig,
                    });
                  } else {
                    setStrategy(currentIndex, {
                      ...currentStrategy,
                      configuration: {
                        ...configuration,
                        redirectTarget: value,
                        redirectDepth: redirectDepth ?? '1',
                      },
                    });
                  }
                }}
                endAdornment={
                  <AdornmentInput
                    value={redirectDepth as string}
                    type={'number'}
                    label={'depth'}
                    disabled={!redirectTarget || redirectTarget === 'direct'}
                    onChange={(e) => {
                      const value = parseInt(e.target.value as string);
                      setStrategy(currentIndex, {
                        ...currentStrategy,
                        configuration: {
                          ...configuration,
                          redirectDepth: Math.max(0, value).toString(),
                        },
                      });
                    }}
                  />
                }
              >
                <MenuItem value={'direct'}>
                  <ListItemText primary={'Direct'} />
                </MenuItem>
                <MenuItem value={'ancestor'}>
                  <ListItemText primary={'Parent Node'} />
                </MenuItem>
                <MenuItem value={'common-ancestor'}>
                  <ListItemText primary={'Common Coordinate'} />
                </MenuItem>
              </StyledSelectField>
            </AdvancedSection>
          </CenteredColumn>
        );
      }
      case NotificationStrategy.MQTT: {
        const { topic } = configuration;

        return (
          <StyledTextField
            type={'text'}
            label={'Topic'}
            value={topic ? (topic as string) : 'ekko/notification'}
            onChange={(e) =>
              setStrategy(currentIndex, {
                ...currentStrategy,
                configuration: {
                  ...configuration,
                  topic: String(e.target.value),
                },
              })
            }
          />
        );
      }
      default:
        return <></>;
    }
  }, [currentStrategy, currentIndex, setStrategy]);

  if (isInheriting) {
    return (
      <Box
        onClick={() => {
          addStrategy(NotificationStrategy.LIGHT);
          setCurrentIndex(0);
        }}
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
          cursor: 'pointer',
          transition: (theme) =>
            theme.transitions.create(['border-color', 'background-color'], {
              easing: theme.transitions.easing.sharp,
              duration: theme.transitions.duration.short,
            }),
          '&:hover': {
            backgroundColor: 'action.hover',
            borderColor: 'text.secondary',
          },
        }}
      >
        <Typography variant="body2" color="text.secondary">
          {inheritLabel ? `Using ${inheritLabel}'s notification strategy` : 'Using inherited notification strategy'}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack direction={'column'} width={'100%'}>
      <ListStepper
        optional={optional}
        index={currentIndex}
        length={strategies.length}
        disableAdd={currentStrategy?.name === NotificationStrategy.NULL}
        onIndexChange={setCurrentIndex}
        onAdd={() => {
          addStrategy(NotificationStrategy.LIGHT);
          setCurrentIndex(strategies.length);
        }}
        onRemove={() => {
          removeStrategy(currentIndex!);
        }}
      />
      {currentStrategy && (
        <Stack direction={'column'} spacing={1} width={'100%'}>
          <StyledSelectField
            value={currentStrategy.name}
            label={'Type'}
            onChange={(e) => {
              if (e.target.value === undefined) {
                setStrategies(undefined);
                return;
              }
              const stratType = e.target.value as NotificationStrategy;
              setStrategy(currentIndex!, {
                name: stratType,
                configuration: {
                  ...(stratType === NotificationStrategy.LIGHT && {
                    color: BlinkColor.RED,
                    duration: 30,
                    bypassQueue: false,
                  }),
                  ...(stratType === NotificationStrategy.MQTT && {
                    topic: '',
                  }),
                },
              });
            }}
          >
            <MenuItem value={NotificationStrategy.LIGHT}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <Highlight />
                <Typography variant="caption" fontWeight={600}>
                  LIGHT
                </Typography>
              </Stack>
            </MenuItem>
            <MenuItem value={NotificationStrategy.NULL}>
              <Stack direction="row" alignItems="center" spacing={1}>
                <NotInterested />
                <Typography variant="caption" fontWeight={600}>
                  NONE
                </Typography>
              </Stack>
            </MenuItem>
          </StyledSelectField>
          <Box sx={{ margin: 0 }}>{configForm}</Box>
        </Stack>
      )}
    </Stack>
  );
};
